import React, { useEffect, useRef } from "react";
import "./App.scss";
import "primereact/resources/themes/md-light-indigo/theme.css";
// import "primereact/resources/themes/md-dark-indigo/theme.css";
import AppRoutes from "./modules/navigation/AppRoute";
import { useDispatch } from "react-redux";
import { rehydrateUser } from "./redux/Slices/authSlice";
import { getUserById } from "./redux/Actions/AuthAction";
import { fetchCategoryFormSettings, fetchLatestCategoryFormSettings } from "./redux/Actions/CategoryFormSettingsAction";
import { fetchUnreadNotificationCount } from "./redux/Slices/UserNotificationSlice";
import { AppDispatch, useAppSelector } from "./redux/store";
import { ToastContainer } from "react-toastify";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { PrimeReactProvider } from "primereact/api";
import { HelmetProvider } from "react-helmet-async";
import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import RevenueLimitNotification from "./components/RevenueLimitNotification";
import NotificationManager from "./components/NotificationManager";
import PopupNotificationManager from "./components/PopupNotificationManager";
import { VerificationProvider } from "./contexts/VerificationContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import GoogleTranslateWidget from "./components/GoogleTranslateWidget";
import "./i18n";

// Debug environment variables
console.log("Environment variables check:");
console.log("REACT_APP_STRIPE_PUBLISHABLE_KEY:", process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
console.log("NODE_ENV:", process.env.NODE_ENV);

// Initialize Stripe with error handling
const stripePromise = (async () => {
    try {
        const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

        if (!publishableKey) {
            console.error("Stripe publishable key is not defined in environment variables");
            return null;
        }

        console.log("Loading Stripe with key:", publishableKey.substring(0, 20) + "...");
        const stripe = await loadStripe(publishableKey);
        console.log("Stripe loaded successfully:", stripe);
        return stripe;
    } catch (error) {
        console.error("Failed to load Stripe:", error);
        return null;
    }
})();

function App() {
    const dispatch = useDispatch<AppDispatch>();
    const { user } = useAppSelector((state) => state.auth);
    const lastFetchedUserIdRef = useRef<string | number | null>(null);

    useEffect(() => {
        dispatch(rehydrateUser());
        // Fetch both regular and latest category form settings
        dispatch(fetchCategoryFormSettings());
        dispatch(fetchLatestCategoryFormSettings());
    }, [dispatch]);

    useEffect(() => {
        if (!user?.id) {
            lastFetchedUserIdRef.current = null;
            return;
        }

        const currentUserId = user.id;

        if (lastFetchedUserIdRef.current === currentUserId) {
            return;
        }

        lastFetchedUserIdRef.current = currentUserId;

        dispatch(getUserById({ id: String(currentUserId), isShowLoader: false })).catch(error => {
            console.error("Failed to refresh authenticated user:", error);
        });
    }, [dispatch, user?.id]);

    // Fetch unread notification count when user is authenticated
    useEffect(() => {
        if (user?.id) {
            dispatch(fetchUnreadNotificationCount(user.id));
        }
    }, [user?.id, dispatch]);

    useEffect(() => {
        const handleWheel = (e: WheelEvent) => {
            if ((e.target as HTMLElement).tagName === "INPUT" &&
                (e.target as HTMLInputElement).type === "number") {
                e.preventDefault();
            }
        };

        // Add listener to the whole document
        document.addEventListener("wheel", handleWheel, { passive: false });

        return () => {
            document.removeEventListener("wheel", handleWheel);
        };
    }, []);

    return (
        <>
            <HelmetProvider>
                <Elements stripe={stripePromise}>
                    <PrimeReactProvider>
                        <LanguageProvider>
                            {/* <GoogleTranslateWidget /> */}
                            <VerificationProvider>
                                <AppRoutes />
                                <RevenueLimitNotification />
                                <NotificationManager />
                                <PopupNotificationManager />
                            </VerificationProvider>
                        </LanguageProvider>
                    </PrimeReactProvider>
                </Elements>
            </HelmetProvider>
            <ToastContainer 
                position="top-right" 
                autoClose={3000} 
                style={{ zIndex: 99999 }}
            />
        </>
    );
}

export default App;
